import { evaluateCondition as evaluateConditionCore } from '@healthblocks-io/core'
import { differenceInYears } from 'date-fns'

import type {
  IQuestionnaireQuestion,
  MultiSelectCommentAnswerItem,
  MultiSelectCommentAnswerOption,
  QuestionAnswerInput,
  SubjectMember,
} from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters, QuestionnaireQuestionWithHealthParameters } from 'index'
import type { AllergyProfile, HealthParameter, MedicationScheme } from '@/modules/app/types/HealthParameter.types'
import type {
  QuestionnaireEngineAnswerValidation,
  QuestionnaireEngineAnswerValidationReason,
} from '../types/questionnaire-engine.types'

/**
 * Checks if an Observation question should be skipped due to missing health parameter data.
 *
 * All health parameter types (general, medication, allergy) use the unified
 * HealthParameter shape with an `observations` array.
 *
 * @param question - The Observation question to check
 * @returns true if the question should be skipped (no data available), false if data exists
 */
export function shouldSkipObservationQuestion(question: QuestionnaireQuestionWithHealthParameters): boolean {
  // If no healthParameter at all, skip the question (no data to show)
  if (!question.healthParameter) {
    return true
  }

  if (!('observations' in question.healthParameter)) {
    return true
  }

  const hp = question.healthParameter as HealthParameter
  const hasObservations =
    Array.isArray(hp.observations) &&
    hp.observations.some((obs) => {
      if (obs.value === undefined || obs.value === null || obs.value === '') return false
      if (obs.type === 'Medication' && (obs.value as MedicationScheme)?.schemeStatus === 'unknown') return false
      return !(obs.type === 'Allergy' && (obs.value as AllergyProfile)?.schemeStatus === 'unknown')
    })

  return !hasObservations
}

/**
 * Evaluates a question's condition, with special handling for Observation questions.
 *
 * Observation questions require special logic because they depend on health parameter data
 * that may or may not be available. This function handles that logic before evaluating
 * the standard condition.
 *
 * @param question - The specific question to evaluate
 * @param answers - Map of answer values keyed by question linkId
 * @returns true if the condition is met (question should be shown), false if it should be skipped
 */
function evaluateQuestionCondition(question: IQuestionnaireQuestion, answers: Record<string, unknown>): boolean {
  // Observation questions can be skipped entirely when there's no external data.
  // Only default to 'edit' when no explicit answer exists — explicit 'keep'/'edit'
  // answers are preserved so child question conditions evaluate correctly.
  if (question.type === 'Observation') {
    const obsQuestion = question as QuestionnaireQuestionWithHealthParameters
    if (shouldSkipObservationQuestion(obsQuestion)) {
      if (obsQuestion.linkId && answers[obsQuestion.linkId] !== 'keep' && answers[obsQuestion.linkId] !== 'edit') {
        answers[obsQuestion.linkId] = 'edit'
      }
      return false
    }
  }

  if (!question.condition) {
    return true
  }

  // Evaluate the standard condition (or the modified condition for Observations)
  return evaluateConditionCore(question.condition, answers)
}

/**
 * Adds demographic data (gender, age) to the answers map for condition evaluation.
 * These values can be used in question conditions (e.g., "show only if female").
 *
 * @param answers - The answers map to enrich
 * @param subject - Optional subject member with demographic data
 */
function addDemographicDataToAnswers(answers: Record<string, unknown>, subject?: SubjectMember): void {
  if (!subject?.gender && !subject?.birthDate) {
    return
  }

  // Normalize gender values
  const gender = subject.gender?.toLowerCase()
  const normalizedGender = ['f', 'female'].includes(gender)
    ? 'female'
    : ['m', 'male'].includes(gender)
      ? 'male'
      : undefined

  // Calculate age from birth date
  const age = subject.birthDate ? differenceInYears(Date.now(), Date.parse(subject.birthDate)) : undefined

  // Add to answers map (demographic data comes first, then user answers)
  Object.assign(answers, {
    subject_gender: normalizedGender,
    subject_age: age,
  })
}

/**
 * Calculates which question IDs should be skipped based on their conditions.
 *
 * Questions are skipped when their condition evaluates to false. This happens when:
 * - The condition references answer values that don't match
 * - The condition references demographic data that doesn't match
 * - For Observation questions: when health parameter data is missing
 *
 * @param questionnaire - The questionnaire with all questions
 * @param history - Array of history entries with current answers
 * @param subject - Optional subject member for demographic data
 * @returns Array of question IDs that should be skipped
 */
export function calculateSkipIds(
  questionnaire: IQuestionnaireWithHealthParameters,
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
  subject: SubjectMember,
): string[] {
  // Step 1: Convert QuestionAnswerInput objects to plain values for condition evaluation
  const answersForEvaluation: Record<string, unknown> = {}

  for (const [linkId, answer] of Object.entries(answers)) {
    if (answer) {
      // Extract the value from QuestionAnswerInput
      answersForEvaluation[linkId] = answer.value
    }
  }

  // Step 2: Add demographic data (gender, age) for condition evaluation
  addDemographicDataToAnswers(answersForEvaluation, subject)

  // Step 3: Evaluate each question's condition and collect IDs to skip
  const skipIds: string[] = []

  for (const question of questionnaire.item) {
    // If condition evaluates to false (or an Observation has no data), question should be skipped
    const conditionResult = evaluateQuestionCondition(question, answersForEvaluation)
    const shouldSkip = !conditionResult

    if (shouldSkip) {
      // Add both id and linkId to skipIds if they exist
      if (question.id) {
        skipIds.push(question.id)
      }
      if (question.linkId && question.linkId !== question.id) {
        skipIds.push(question.linkId)
      }

      // Clear answers for skipped non-Observation questions so downstream
      // conditions don't reference values from hidden questions.
      // Observation answers are preserved because their 'keep'/'edit' values
      // are intentionally used by child question conditions.
      if (question.type !== 'Observation') {
        const linkId = question.linkId || question.id
        if (linkId) {
          delete answersForEvaluation[linkId]
        }
      }
    }
  }

  return skipIds
}

/**
 * Returns true when a MultiSelectComment answer selects an option whose
 * `isCommentRequired` flag is set while its item has no meaningful comment.
 */
export function hasMissingRequiredComment(
  question: IQuestionnaireQuestion,
  items: MultiSelectCommentAnswerItem[],
): boolean {
  const options = (question.answerOption ?? []) as MultiSelectCommentAnswerOption[]

  return items.some((item) => {
    const option = options.find((o) => o.value === item.value)
    return option?.isCommentRequired && !item.comment?.trim()
  })
}

/**
 * Validates all questionnaire answers and returns detailed validation results.
 *
 * @param answerableQuestions - The questions that are visible/answerable (already filtered by conditions)
 * @param answers - Map of answer values keyed by question linkId
 * @param subject - Subject member for demographic data
 * @returns Validation result dictionary keyed by linkId with validation details for each answer
 */
export function validateQuestionnaireAnswers(
  answerableQuestions: IQuestionnaireQuestion[],
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
  subject: SubjectMember,
): QuestionnaireEngineAnswerValidation {
  // Step 1: Prepare answers for evaluation (convert QuestionAnswerInput to plain values)
  const answersForEvaluation: Record<string, unknown> = {}
  for (const [linkId, answer] of Object.entries(answers)) {
    if (answer) {
      answersForEvaluation[linkId] = answer.value
    }
  }
  addDemographicDataToAnswers(answersForEvaluation, subject)

  // Step 2: Validate each question
  // Note: answerableQuestions are already filtered (non-skipped questions), so we don't need to check skipIds again
  const validations: QuestionnaireEngineAnswerValidation = {}

  for (const question of answerableQuestions) {
    const linkId = question.linkId ?? question.id
    if (!linkId) continue

    // Skip Display questions (they can't be answered)
    if (question.type === 'Display') {
      continue
    }

    // Validate visible questions
    const answer = answers[linkId]
    const answerValue = answer?.value

    const validation = {
      isValid: true,
      value: answerValue,
    } as {
      isValid: boolean
      value: unknown
      reason?: QuestionnaireEngineAnswerValidationReason
    }

    // Check required field
    if (question.required) {
      const hasAnswer = answerValue !== undefined && answerValue !== null && answerValue !== ''

      // For arrays, check if at least one item
      if (Array.isArray(answerValue)) {
        if (answerValue.length === 0) {
          validation.isValid = false
          validation.reason = 'required'
        }
      } else if (!hasAnswer) {
        validation.isValid = false
        validation.reason = 'required'
      }
    }

    // If already invalid (required), skip format validations
    if (!validation.isValid) {
      validations[linkId] = validation
      continue
    }

    // Format validations only apply if there's an answer
    if (answerValue !== undefined && answerValue !== null && answerValue !== '') {
      // Text length validations
      if (typeof answerValue === 'string') {
        if (question.maxlength !== undefined && answerValue.length > question.maxlength) {
          validation.isValid = false
          validation.reason = 'maxlength'
        }
        if (question.minlength !== undefined && answerValue.length < question.minlength) {
          validation.isValid = false
          validation.reason = 'minlength'
        }
      }

      // Number validations
      if (question.type === 'Number' && typeof answerValue === 'number') {
        if (question.min !== undefined && answerValue < question.min) {
          validation.isValid = false
          validation.reason = 'min'
        }
        if (question.max !== undefined && answerValue > question.max) {
          validation.isValid = false
          validation.reason = 'max'
        }
      }

      // Array validations (for MultiSelect, Array types)
      if (Array.isArray(answerValue) && question.required && answerValue.length === 0) {
        validation.isValid = false
        validation.reason = 'required'
      }

      // MultiSelectComment: options with isCommentRequired must carry a
      // non-empty comment when selected. Whitespace-only counts as empty
      // (persisted answers never contain such comments, but in-progress
      // form values can).
      if (
        question.type === 'MultiSelectComment' &&
        Array.isArray(answerValue) &&
        hasMissingRequiredComment(question, answerValue)
      ) {
        validation.isValid = false
        validation.reason = 'commentRequired'
      }
    }

    validations[linkId] = validation
  }

  return validations
}
